import { LOGO_URL, APP_NAME, COMPANY_NAME, CONTACT_EMAIL, CONTACT_PHONE } from '../lib/constants';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const lastUpdated = "January 3, 2025";
  const dpoEmail = "DPO@myprivicy.com";
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={APP_NAME} className="h-8" />
            <span className="font-bold text-[#0F172A] font-['Manrope']">{APP_NAME}</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#64748B]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2 font-['Manrope']">
            Privacy Policy
          </h1>
          <p className="text-[#64748B] mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">1. Introduction</h2>
              <p className="text-[#475569] mb-4">
                {COMPANY_NAME} ("we", "us", or "our") is committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                you use {APP_NAME} (the "Service").
              </p>
              <p className="text-[#475569] mb-4">
                This policy is designed to comply with the data protection laws of the jurisdictions we serve, including:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Jamaica:</strong> Data Protection Act, 2020</li>
                <li><strong>Barbados:</strong> Data Protection Act, 2019</li>
                <li><strong>Bermuda:</strong> Personal Information Protection Act, 2016 (PIPA)</li>
                <li><strong>Cayman Islands:</strong> Data Protection Act, 2017</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">2. Data Controller</h2>
              <p className="text-[#475569] mb-4">
                {COMPANY_NAME} is the data controller responsible for your personal data. Our Data Protection 
                Officer can be contacted at:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="text-[#0F172A] font-semibold">Data Protection Officer</p>
                <p className="text-[#475569]">{COMPANY_NAME}</p>
                <p className="text-[#3B82F6]">
                  <a href={`mailto:${dpoEmail}`}>{dpoEmail}</a>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">3. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-[#0F172A] mb-3">3.1 Information You Provide</h3>
              <p className="text-[#475569] mb-4">When you register for and use our Service, we collect:</p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, phone number, company name, country</li>
                <li><strong>Assessment Data:</strong> Responses to compliance assessment questionnaires</li>
                <li><strong>Communication Data:</strong> Information you provide when contacting us</li>
              </ul>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-3">3.2 Information Collected Automatically</h3>
              <p className="text-[#475569] mb-4">When you access our Service, we automatically collect:</p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent, features used</li>
                <li><strong>Log Data:</strong> IP address, access times, referring URLs</li>
                <li><strong>Cookies and Similar Technologies:</strong> See our Cookie Policy for details</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">4. Legal Basis for Processing</h2>
              <p className="text-[#475569] mb-4">We process your personal data based on the following legal grounds:</p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Consent:</strong> When you create an account and accept our Terms and Privacy Policy</li>
                <li><strong>Contract:</strong> To provide the services you have requested</li>
                <li><strong>Legitimate Interest:</strong> To improve our services, prevent fraud, and ensure security</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">5. How We Use Your Information</h2>
              <p className="text-[#475569] mb-4">We use your personal data to:</p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Provide, maintain, and improve our compliance assessment services</li>
                <li>Create and manage your user account</li>
                <li>Generate compliance reports and action plans</li>
                <li>Communicate with you about our services, updates, and support</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">6. Data Sharing and Disclosure</h2>
              <p className="text-[#475569] mb-4">We may share your personal data with:</p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Service Providers:</strong> Third parties who assist us in operating our Service (hosting, analytics, email)</li>
                <li><strong>Professional Advisors:</strong> Lawyers, accountants, and consultants as needed</li>
                <li><strong>Legal Authorities:</strong> When required by law, court order, or governmental request</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
              <p className="text-[#475569] mb-4">
                We do not sell, rent, or trade your personal data to third parties for marketing purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">7. International Data Transfers</h2>
              <p className="text-[#475569] mb-4">
                Your personal data may be transferred to and processed in countries outside your country of 
                residence, including countries that may not provide the same level of data protection. When 
                we transfer your data internationally, we implement appropriate safeguards including:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Standard contractual clauses approved by relevant authorities</li>
                <li>Data processing agreements with appropriate security requirements</li>
                <li>Ensuring recipients maintain adequate data protection measures</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">8. Data Retention</h2>
              <p className="text-[#475569] mb-4">
                We retain your personal data for as long as necessary to fulfill the purposes outlined in 
                this Privacy Policy, unless a longer retention period is required by law. Specifically:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Account Data:</strong> Retained while your account is active and for 3 years after account closure</li>
                <li><strong>Assessment Data:</strong> Retained for 5 years from completion for audit purposes</li>
                <li><strong>Communication Records:</strong> Retained for 3 years</li>
                <li><strong>Log Data:</strong> Retained for 12 months</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">9. Your Rights</h2>
              <p className="text-[#475569] mb-4">
                Depending on your jurisdiction, you may have the following rights regarding your personal data:
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-[#0F172A] mb-2">Under Caribbean Data Protection Laws:</h4>
                <ul className="list-disc pl-6 text-[#475569] space-y-2">
                  <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                  <li><strong>Right to Erasure:</strong> Request deletion of your data in certain circumstances</li>
                  <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
                  <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Right to Object:</strong> Object to processing based on legitimate interest</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
                </ul>
              </div>
              
              <p className="text-[#475569] mb-4">
                To exercise any of these rights, please contact our Data Protection Officer at{' '}
                <a href={`mailto:${dpoEmail}`} className="text-[#3B82F6] hover:underline">{dpoEmail}</a>.
                We will respond to your request within 30 days (or as required by applicable law).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">10. Data Security</h2>
              <p className="text-[#475569] mb-4">
                We implement appropriate technical and organizational measures to protect your personal data, including:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure password hashing</li>
                <li>Access controls and authentication measures</li>
                <li>Regular security assessments and updates</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="text-[#475569] mb-4">
                While we strive to protect your personal data, no method of transmission over the Internet 
                or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">11. Children's Privacy</h2>
              <p className="text-[#475569] mb-4">
                Our Service is not directed to individuals under the age of 18. We do not knowingly collect 
                personal data from children. If you become aware that a child has provided us with personal 
                data, please contact us at{' '}
                <a href={`mailto:${dpoEmail}`} className="text-[#3B82F6] hover:underline">{dpoEmail}</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">12. Changes to This Policy</h2>
              <p className="text-[#475569] mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">13. Complaints</h2>
              <p className="text-[#475569] mb-4">
                If you have concerns about how we handle your personal data, please contact our Data Protection 
                Officer first. If you are not satisfied with our response, you have the right to lodge a 
                complaint with the relevant data protection authority in your jurisdiction:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Jamaica:</strong> Office of the Information Commissioner</li>
                <li><strong>Barbados:</strong> Data Protection Commissioner</li>
                <li><strong>Bermuda:</strong> Privacy Commissioner for Bermuda</li>
                <li><strong>Cayman Islands:</strong> Ombudsman (Data Protection)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">14. Contact Us</h2>
              <p className="text-[#475569] mb-4">
                For questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-[#0F172A] font-semibold mb-2">{COMPANY_NAME}</p>
                <p className="text-[#475569] flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3B82F6] hover:underline">{CONTACT_EMAIL}</a>
                </p>
                <p className="text-[#475569] flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${dpoEmail}`} className="text-[#3B82F6] hover:underline">{dpoEmail}</a> (Data Protection Officer)
                </p>
                <p className="text-[#475569] flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {CONTACT_PHONE}
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-[#64748B]">
          <Link to="/terms" className="hover:text-[#3B82F6]">Terms and Conditions</Link>
          <Link to="/cookies" className="hover:text-[#3B82F6]">Cookie Policy</Link>
          <Link to="/" className="hover:text-[#3B82F6]">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
